import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useCountry } from "../context/CountryContext";

export default function TransferToOpay() {
  const { isGhana, config, country } = useCountry();

  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState(null);
  const [resolvedBank, setResolvedBank] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [txnId, setTxnId] = useState(null);
  const [txnTime, setTxnTime] = useState(null);
  const [error, setError] = useState(null);
  const [accountLockError, setAccountLockError] = useState(null);

  const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        bgColor: "bg-black",
        primaryBtn: "bg-yellow-500 hover:bg-yellow-600 text-black",
        accentText: "text-yellow-400",
        accentBorder: "focus:ring-yellow-500",
        confirmBtn: "bg-yellow-500 hover:bg-yellow-600",
        successCircle: "bg-yellow-500",
        successText: "text-yellow-400",
        bottomBtn: "bg-yellow-500 hover:bg-yellow-600",
        bottomBtnDark: "bg-yellow-900",
        currencySymbol: "₵",
        flag: "🇬🇭",
        title: "Transfer to Mobile Money / Bank",
        accountLabel: "Enter Mobile Number / Account",
        bankPlaceholder: "Search Ghanaian banks & Mobile Money...",
        countryCode: "GH",
      }
    : {
        bgColor: "bg-black",
        primaryBtn: "bg-green-600 hover:bg-green-700 text-white",
        accentText: "text-green-400",
        accentBorder: "focus:ring-green-500",
        confirmBtn: "bg-green-600 hover:bg-green-700",
        successCircle: "bg-green-600",
        successText: "text-green-500",
        bottomBtn: "bg-green-600 hover:bg-green-700",
        bottomBtnDark: "bg-green-900",
        currencySymbol: "₦",
        flag: "🇳🇬",
        title: "Transfer to Bank Account",
        accountLabel: "Enter 10-digit Account Number",
        bankPlaceholder: "Search Nigerian banks...",
        countryCode: "NG",
      };

  // ✅ Fetch bank list — LOCKED to user's country
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await fetch(`${API_BASE}/bank/list?country=${theme.countryCode}`);
        const data = await res.json();

        setBanks(
          (Array.isArray(data) ? data : []).map((bank) => ({
            value: bank.code,
            label: bank.name,
            isMobileMoney: bank.isMobileMoney || false,
          }))
        );
      } catch (err) {
        console.error("⚠️ Error fetching banks:", err);
      }
    };
    fetchBanks();
  }, [theme.countryCode]);

  // ✅ Auto resolve account name
  useEffect(() => {
    const resolveAccount = async () => {
      const requiredLength = isGhana ? 10 : 10;
      if (accountNumber.length === requiredLength && selectedBank) {
        setLoading(true);
        try {
          const res = await fetch(`${API_BASE}/bank/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_number: accountNumber,
              bank_code: selectedBank,
              country: theme.countryCode,
            }),
          });
          const data = await res.json();
          if (data.account_name) {
            setResolvedName(data.account_name);
            setResolvedBank(data.bank_name);
          } else {
            setResolvedName("❌ Account not found");
            setResolvedBank(null);
          }
        } catch (err) {
          setResolvedName("⚠️ Error resolving account");
          setResolvedBank(null);
        } finally {
          setLoading(false);
        }
      } else {
        setResolvedName(null);
        setResolvedBank(null);
      }
    };
    resolveAccount();
  }, [accountNumber, selectedBank, isGhana, theme.countryCode]);

  // ✅ Handle transfer
  const handleTransfer = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    // ✅ Check if account is locked LOCALLY first
    if (user?.accountLocked) {
      setAccountLockError(
        user?.accountLockReason ||
          "Your account has been temporarily locked. Please contact support."
      );
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/bank/wallet/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          account_number: accountNumber,
          bank_code: selectedBank,
          account_name: resolvedName,
          amount,
          country: theme.countryCode,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setTxnId("TXN-" + Date.now());
        setTxnTime(new Date().toLocaleString());
        setStep("receipt");
      } else {
        // ✅ Check if it's an account lock from backend
        if (
          res.status === 403 ||
          data.accountLocked ||
          (data.message && data.message.toLowerCase().includes("lock"))
        ) {
          setAccountLockError(
            data.message ||
              data.error ||
              "Your account has been temporarily locked. Please contact support."
          );
        } else {
          setError(data.error || data.message || "Transfer failed");
        }
      }
    } catch (err) {
      console.error("⚠️ Transfer error:", err);
      setError("Transfer failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${theme.bgColor} min-h-screen text-white px-4 py-6`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* ⚠️ Account Locked Modal — descending bottom sheet */}
      {accountLockError && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60">
          <div className="w-full max-w-md bg-gray-900 rounded-t-3xl p-6 animate-slideUp">
            <div className="w-12 h-1.5 bg-gray-600 rounded-full mx-auto mb-4"></div>

            <div className="flex justify-center mb-3">
              <div className="bg-red-600 p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  className="w-7 h-7"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 1a4 4 0 014 4v3h1a3 3 0 013 3v9a3 3 0 01-3 3H7a3 3 0 01-3-3v-9a3 3 0 013-3h1V5a4 4 0 014-4zm0 2a2 2 0 00-2 2v3h4V5a2 2 0 00-2-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-center text-lg font-bold mb-2">
              🔒 Account Locked
            </h2>

            <p className="text-center text-gray-300 text-sm mb-3">
              You cannot perform this transfer at the moment.
            </p>

            <div className="bg-red-500/10 border border-red-400/30 rounded-xl p-3 mb-5">
              <p className="text-center text-red-300 text-sm font-medium">
                Reason:
              </p>
              <p className="text-center text-red-200 text-sm mt-1">
                {accountLockError}
              </p>
            </div>

            <button
              onClick={() => setAccountLockError(null)}
              className={`w-full ${theme.primaryBtn} py-3 rounded-xl font-semibold transition`}
            >
              I Understand
            </button>

            <p className="text-center text-xs text-gray-500 mt-3">
              Contact support if you need help
            </p>
          </div>
        </div>
      )}

      {/* ⚠️ General Error Modal */}
      {error && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60">
          <div className="w-full max-w-md bg-gray-900 rounded-t-3xl p-6 animate-slideUp">
            <div className="w-12 h-1.5 bg-gray-600 rounded-full mx-auto mb-4"></div>

            <div className="flex justify-center mb-3">
              <div className="bg-red-600 p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  className="w-6 h-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 13h-2v2h2v-2zm0-8h-2v6h2V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-center text-lg font-semibold mb-1">Transaction Failed</h2>
            <p className="text-center text-red-400 text-sm mb-5">{error}</p>

            <button
              onClick={() => setError(null)}
              className="w-full bg-red-600 hover:bg-red-700 py-3 rounded-xl font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      <h2 className="text-lg font-semibold mb-1">{theme.title}</h2>
      <p className="text-xs text-gray-400 mb-5">
        {theme.flag} {config.name} only · {theme.currencySymbol} {config.currency}
      </p>

      {/* STEP 1: FORM */}
      {step === "form" && (
        <div className="space-y-3 mb-5">
          {/* Account number */}
          <input
            type="text"
            maxLength={isGhana ? 13 : 10}
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
            placeholder={theme.accountLabel}
            className={`w-full bg-gray-900 text-white p-3 rounded-xl outline-none focus:ring-2 ${theme.accentBorder}`}
          />

          {/* Bank dropdown — LOCKED to user's country */}
          <Select
            options={banks}
            value={banks.find((b) => b.value === selectedBank) || null}
            onChange={(option) => setSelectedBank(option ? option.value : "")}
            placeholder={theme.bankPlaceholder}
            isSearchable={true}
            className="w-full text-black rounded-xl"
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "#111827",
                borderRadius: "0.75rem",
                padding: "4px",
                border: "none",
                boxShadow: "none",
              }),
              singleValue: (base) => ({ ...base, color: "white" }),
              menu: (base) => ({ ...base, backgroundColor: "#111827" }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? "#374151" : "#111827",
                color: "white",
              }),
              placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
            }}
          />

          {loading && <p className="text-yellow-400 text-sm">⏳ Resolving...</p>}
          {resolvedName && (
            <p className={`${resolvedName.startsWith("❌") || resolvedName.startsWith("⚠️") ? "text-red-400" : "text-green-400"} font-medium`}>
              {resolvedName.startsWith("❌") || resolvedName.startsWith("⚠️") ? "" : "✅"} {resolvedName} {resolvedBank ? `(${resolvedBank})` : ""}
            </p>
          )}

          {/* Amount with currency symbol */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
              {theme.currencySymbol}
            </span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter Amount"
              className={`w-full bg-gray-900 text-white pl-9 pr-4 py-3 rounded-xl outline-none focus:ring-2 ${theme.accentBorder}`}
            />
          </div>

          <button
            onClick={() => setStep("confirm")}
            className={`w-full ${theme.confirmBtn} py-3 rounded-xl font-semibold disabled:opacity-50`}
            disabled={!resolvedName || resolvedName?.startsWith("❌") || !amount}
          >
            Continue
          </button>
        </div>
      )}

      {/* STEP 2: CONFIRM */}
      {step === "confirm" && (
        <div className="bg-gray-900 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-3">Confirm Transfer</h3>
          <p className="mb-2">
            Country: <span className={theme.accentText}>{theme.flag} {config.name}</span>
          </p>
          <p className="mb-2">
            Bank: <span className={theme.accentText}>{resolvedBank}</span>
          </p>
          <p className="mb-2">
            Account: <span className={theme.accentText}>{accountNumber}</span>
          </p>
          <p className="mb-2">
            Name: <span className={theme.accentText}>{resolvedName}</span>
          </p>
          <p className="mb-2">
            Amount: <span className={theme.accentText}>{theme.currencySymbol}{Number(amount).toLocaleString()}</span>
          </p>

          <div className="flex space-x-3 mt-5">
            <button
              onClick={() => setStep("form")}
              className="flex-1 bg-gray-700 py-3 rounded-xl"
            >
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              className={`flex-1 ${theme.confirmBtn} py-3 rounded-xl`}
              disabled={loading}
            >
              {loading ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: RECEIPT */}
      {step === "receipt" && (
        <div className="bg-gray-950 text-white p-5 rounded-2xl shadow-lg">
          <div className="bg-gray-900 p-5 rounded-2xl text-center mb-5">
            <div className="flex justify-center mb-2">
              <div className={`${theme.successCircle} p-3 rounded-full`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="white"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75"
                  />
                </svg>
              </div>
            </div>

            <p className="text-sm opacity-80 mb-1">
              Transfer to {resolvedName?.toUpperCase()}
            </p>
            <h1 className="text-3xl font-bold text-white">
              {theme.currencySymbol}{Number(amount).toLocaleString()}
            </h1>
            <p className={`${theme.successText} font-semibold mt-1`}>Successful</p>
          </div>

          <div className="bg-gray-900 p-5 rounded-2xl mb-5">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Transaction Details</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Recipient Details</span>
                <span className="text-right font-medium text-white">
                  {resolvedName?.toUpperCase()}
                  <br />
                  {resolvedBank} | {accountNumber}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Transaction No.</span>
                <span className="text-right text-white">{txnId}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Payment Method</span>
                <span className={`text-right ${theme.accentText} font-semibold`}>
                  Wallet &gt;
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Country</span>
                <span className="text-right">{theme.flag} {config.name}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-400">Transaction Date</span>
                <span className="text-right">{txnTime}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 p-5 rounded-2xl mb-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">More Actions</h3>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Category</span>
              <span className={`${theme.accentText} font-semibold`}>Transfer &gt;</span>
            </div>

            <div className={`flex justify-between mt-4 ${theme.accentText} font-medium`}>
              <button
                onClick={() => {
                  setStep("form");
                  setAccountNumber("");
                  setSelectedBank("");
                  setAmount("");
                  setResolvedName(null);
                  setResolvedBank(null);
                }}
                className="flex items-center space-x-1 hover:underline"
              >
                <span>🔁</span> <span>Transfer Again</span>
              </button>
              <button className="flex items-center space-x-1 hover:underline">
                <span>🕓</span> <span>View Records</span>
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <button className={`flex-1 ${theme.bottomBtnDark} py-3 rounded-xl text-white font-semibold`}>
              Report Issue
            </button>
            <button
              onClick={() => alert("📤 Receipt shared!")}
              className={`flex-1 ${theme.bottomBtn} py-3 rounded-xl text-white font-semibold`}
            >
              Share Receipt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
